<template>
    <div>

        <simple-dropdown
            position="right"
            width="320"
        >
            <template #trigger="{ isOpen }">
                <simple-button
                    display="primary"
                    look="primary"
                    :icon="isOpen ? 'keyboard-arrow-up' : 'keyboard-arrow-down'"
                    :disabled="disabled"
                >
                    {{ $t('general.actions') }}
                </simple-button>
            </template>
            <template #content>
                <ul
                    aria-labelledby="option-menu"
                    aria-orientation="vertical"
                    role="menu"
                    class="max-h-320 overflow-y-auto"
                >
                    <li
                        v-for="item in items"
                        class="py-8 px-12 group flex items-center gap-8 cursor-pointer text-black hover:text-white"
                        :class="[item.danger ? 'hover:bg-semantic-error' : 'hover:bg-primary']"
                        @click="item.action"
                    >
                        <svg-icon
                            v-if="item.icon"
                            :type="item.icon"
                            class="w-18 h-18 fill-primary group-hover:fill-white"
                        />
                        <span class="first-letter:capitalize">{{ item.title }}</span>
                    </li>
                </ul>
            </template>
        </simple-dropdown>

        <application-exporter
            v-if="exporting"
            :applications="selectedApplicationIds"
            :request-filters="getFilters"
            :stages="selectedStageIds"
            @close="stopExport"
        ></application-exporter>

        <mail-creator
            v-if="sendingMail"
            :context="singleJob ? 'jobs' : 'applications'"
            :applications="selectedApplications"
            :request-filters="getFilters"
            :stages="selectedStageIds"
            :total-applications="totalApplications"
            @close="cancelEmail"
        ></mail-creator>

        <invitation-creator
            v-if="can.manage && sendingInterview"
            :context="singleJob ? 'jobs' : 'applications'"
            :applications="selectedApplications"
            :request-filters="getFilters"
            :stages="selectedStageIds"
            :total-applications="totalApplications"
            @close="cancelInterview"
        />

        <popup-modal-confirm
            v-if="rejecting"
            custom-classes="w-624"
            :confirm-text="$t('applications.reject_subject', {subject: $t('applications.selected_applications')})"
            :message="$tc('applications.confirm_reject', bulkKeyCount, {count: bulkKeyCount})"
            show-exclamation
            :title="$tc('applications.confirm_reject', bulkKeyCount, {count: bulkKeyCount})"
            :working="performingRejectRequest"
            @close="cancelReject"
            @confirm="reject"
        >
            <rejected-reason v-model="rejectData" :has-external-partner="selectedApplicationHasExternalPartner"/>
        </popup-modal-confirm>
        <popup-modal-confirm
            v-if="notRejecting"
            :confirm-text="$t('applications.not_reject_subject')"
            :fetching-data="!bulkKeyData"
            :title="$tc('applications.confirm_not_reject', bulkKeyCount, {count: bulkKeyCount})"
            :working="performingNotRejectRequest"
            confirm-button="success"
            @close="cancelNotReject"
            @confirm="notReject"
        >
        </popup-modal-confirm>

        <popup-modal-confirm
            v-if="anonymising"
            :confirm-text="$t('applications.do_anonymise')"
            :fetching-data="!bulkKeyData"
            :message="$tc('applications.anonymise_confirm_bulk', bulkKeyCount) + ' ' + $t('applications.anonymise_confirm_message')"
            show-exclamation
            :title="$t('applications.anonymise_title')"
            :working="performingAnonymisationRequest"
            @close="cancelAnonymise"
            @confirm="anonymise"
        >
        </popup-modal-confirm>

        <popup-modal-confirm
            v-if="subscribing"
            :confirm-text="$t('applications.subscribe_subject', {subject: $t('applications.selected_applications')})"
            :fetching-data="!bulkKeyData"
            :message="$tc('applications.confirm_subscribe', bulkKeyCount, {count: bulkKeyCount})"
            :working="performingSubscribeRequest"
            confirm-button="success"
            @close="cancelSubscribe"
            @confirm="subscribe"
        ></popup-modal-confirm>

        <popup-modal-confirm
            v-if="unsubscribing"
            :confirm-text="$t('applications.unsubscribe_subject', {subject: $t('applications.selected_applications')})"
            :fetching-data="!bulkKeyData"
            :message="$tc('applications.confirm_unsubscribe', bulkKeyCount, {count: bulkKeyCount})"
            :working="performingUnsubscribeRequest"
            confirm-button="success"
            @close="cancelUnsubscribe"
            @confirm="unsubscribe"
        ></popup-modal-confirm>

        <move-to-job
            v-if="movingApplications"
            :applications="selectedApplicationIds"
            :defaultJob="singleJob"
            :request-filters="getFilters"
            :stages="selectedStageIds"
            @close="stopMovingApplications"
        ></move-to-job>

        <change-source-applications
            v-if="changingSourceApplications"
            :applications="selectedApplicationIds"
            :request-filters="getFilters"
            :stages="selectedStageIds"
            @close="toggleChangingSourceApplications(false)"
        ></change-source-applications>

    </div>
</template>

<script>
import SimpleDropdown from './SimpleDropdown.vue';
import SvgIcon from './SvgIcon.vue';
import BulkActionButton from './BulkActionButton.vue';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useAuthenticationStore} from '../../state/modules/authentication';
import {useNotificationsStore} from '../../state/modules/notifications';
import {useApplicationsStore} from '../../state/modules/applications';
import ApplicationExporter from './ApplicationExporter.vue';
import PopupModalConfirm from './PopupModalConfirm.vue';
import * as applications from '../../services/ApplicationService';
import Application from '../../models/Application';
import Subscription from '../../models/Subscription';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import MailCreator from './MailCreator.vue';
import {normalizeJson} from '../../../shared/helpers';
import MoveToJob from './MoveToJob.vue';
import ChangeSourceApplications from './ChangeSourceApplications.vue';
import Job from '../../models/Job';
import * as bulkApplicationKey from '../../services/BulkApplicationKeyService';
import RejectedReason from './RejectedReason';
import Stage from '../../models/Stage';
import InvitationCreator from './InvitationCreator.vue';
import SelectDropdown from "../forms/SelectDropdown.vue";
import SimpleButton from "./SimpleButton.vue";
import {useJobsStore} from "../../state/modules/jobs";

export default {
    components: {
        SimpleButton,
        InvitationCreator,
        RejectedReason,
        SelectDropdown,
        'simple-dropdown': SimpleDropdown,
        'svg-icon': SvgIcon,
        'action-button': BulkActionButton,
        'application-exporter': ApplicationExporter,
        'popup-modal-confirm': PopupModalConfirm,
        'mail-creator': MailCreator,
        'move-to-job': MoveToJob,
        'change-source-applications': ChangeSourceApplications
    },
    props: {
        disabled: Boolean,
        requestFilters: {
            type: Object,
            default: () => {
            },
        },
        selection: {
            type: Object,
            default: () => ({
                applications: [],
                stages: [],
            }),
        },
        totalApplications: {
            type: Number,
            default: 0,
        },
        type: {
            type: String,
            default: 'selection',
            validator: value => ['filter', 'selection', 'both'].includes(value),
        },
        singleJob: Job,
    },
    data: function () {
        return {
            sendingMail: false,
            sendingInterview: false,
            exporting: false,
            rejecting: false,
            anonymising: false,
            notRejecting: false,
            performingAnonymisationRequest: false,
            performingRejectRequest: false,
            performingNotRejectRequest: false,
            performingSubscribeRequest: false,
            performingUnsubscribeRequest: false,
            subscribing: false,
            unsubscribing: false,
            movingApplications: false,
            changingSourceApplications: false,
            bulkKeyData: null,
            rejectData: {
                reason: null,
                comment: '',
                comment_external: '',
            }
        };
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        ...mapState(useAuthenticationStore, ['currentUserId']),
        selectedStageIds() {
            return this.selection['stages'] || [];
        },
        getFilters() {
            return ['filter', 'both'].includes(this.type) ? this.requestFilters : {};
        },
        selectedApplications() {
            return ['selection', 'both'].includes(this.type) ? this.selection['applications'] || [] : [];
        },
        selectedApplicationIds() {
            return this.selectedApplications.map(app => app.id);
        },
        selectedApplicationHasExternalPartner() {
            return this.selectedApplications.some(app => app.interim_candidate_id > 0);
        },
        can() {
            return {
                reject: this.singleJob ? this.$can(['applications.manage', 'job-applications.manage.' + this.singleJob?.id]) : true,
                notReject: this.singleJob ? this.$can(['applications.manage', 'job-applications.manage.' + this.singleJob?.id]) : true,
                manage: this.singleJob ? this.$can(['applications.manage', 'job-applications.manage.' + this.singleJob?.id]) : true,
            };
        },
        bulkKeyCount() {
            return this.bulkKeyData ? this.bulkKeyData.attributes.applications_count : 0;
        },
        items() {
            return [
                {
                    title: this.$t('applications.email_subject', {subject: this.$t('applications.selected_applications')}),
                    icon: 'mail',
                    action: this.startEmail,
                },
                {
                    title: this.$t('applications.interview_subject', {subject: this.$t('applications.selected_applications')}),
                    icon: 'calendar-today',
                    action: this.startInterview,
                },
                {
                    title: this.$t('applications.export_csv_subject', {subject: this.$t('applications.selected_applications')}),
                    icon: 'download',
                    action: this.startExport,
                },
                {
                    title: this.$t('applications.turn_on_notifications'),
                    icon: 'notifications',
                    action: this.confirmSubscribe,
                },
                {
                    title: this.$t('applications.turn_off_notifications'),
                    icon: 'notifications',
                    action: this.confirmUnsubscribe,
                },
                {
                    title: this.$t('applications.not_reject_subject'),
                    icon: 'check-circle',
                    action: this.confirmNotReject,
                    condition: this.can.notReject,
                },
                {
                    title: this.$t('applications.move_subject', {subject: this.$t('applications.selected_applications')}),
                    icon: 'swap-vert',
                    action: this.startMovingApplications,
                },
                {
                    title: this.$t('applications.change_source'),
                    icon: 'link',
                    action: this.toggleChangingSourceApplications,
                },
                {
                    title: this.$t('applications.anonymise'),
                    icon: 'anonymised',
                    action: this.confirmAnonymise,
                },
                {
                    title: this.$t('applications.reject_subject', {subject: this.$t('applications.selected_applications')}),
                    icon: 'front-hand',
                    action: this.confirmReject,
                    condition: this.can.reject,
                    danger: true,
                },
            ]
        },
    },
    methods: {
        ...mapActions(useJobsStore, ['setSelectedApplications']),
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        ...mapActions(useApplicationsStore, ['clearCheckedApplications']),
        startEmail() {
            this.sendingMail = true;
        },
        cancelEmail() {
            this.sendingMail = false;
        },
        startInterview() {
            this.sendingInterview = true;
        },
        cancelInterview() {
            this.sendingInterview = false;
        },
        startExport() {
            this.exporting = true;
        },
        stopExport() {
            this.exporting = false;
        },
        confirmReject() {
            this.rejecting = true;
            this.getBulkKey();
        },
        confirmAnonymise() {
            this.anonymising = true;
            this.getBulkKey();
        },
        confirmNotReject() {
            this.notRejecting = true;
            this.getBulkKey();
        },
        cancelReject() {
            this.rejectData = {
                reason: null,
                comment: '',
                comment_external: '',
            };
            this.rejecting = false;
            this.bulkKeyData = null;
        },
        cancelNotReject() {
            this.notRejecting = false;
            this.bulkKeyData = null;
        },
        cancelAnonymise() {
            this.anonymising = false;
            this.bulkKeyData = null;
        },
        anonymise() {
            this.performingAnonymisationRequest = true;

            applications.massAnonymise(this.getFilters, {
                applications: this.selectedApplicationIds,
                stages: this.selectedStageIds,
            }, this.bulkKeyData.id).then(result => {
                let rejected = this.getFilters['filter[rejected]'];

                if (result?.data?.message === 'started') {
                    this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('bulkaction.application.massAnonymise.started'),
                    });
                    return;
                }

                Object.keys(result.data.stage_anonymisations).forEach(stageId => {
                    stageId = Number(stageId);
                    let stage = useRepo(Stage).where('id', stageId).first(),
                        anonymisedCount = result.data.stage_anonymisations[stageId],
                        rejectedCount = 0,
                        applicationsCount = 0;

                    if (rejected !== null && (rejected === true || rejected === 'all')) {
                        rejectedCount = result.data.stage_anonymisations[stageId];
                    } else {
                        applicationsCount = result.data.stage_anonymisations[stageId];
                    }

                    useRepo(Stage).save({
                        id: stageId,
                        anonymised_count: stage.anonymised_count + anonymisedCount,
                        rejected_count: stage.rejected_count + rejectedCount,
                        applications_count: stage.applications_count + applicationsCount,
                    });
                });

                return useRepo(Application).whereIn('id', result.data.updated_application_ids).delete();
            }).then(updates => {
                this.anonymising = false;
                this.performingAnonymisationRequest = false;
                this.clearCheckedApplications();
            });
        },
        reject() {
            this.performingRejectRequest = true;

            applications.massReject(this.getFilters, {
                applications: this.selectedApplicationIds,
                stages: this.selectedStageIds,
            }, {rejected: true, reject_reason: this.rejectData.reason, reject_comment: this.rejectData.comment, reject_comment_external: this.rejectData.comment_external}, this.bulkKeyData.id)
                .then(result => {
                    if (result?.data?.message === 'started') {
                        this[ADD_NOTIFICATION]({
                            type: 'success',
                            message: this.$t('bulkaction.application.massReject.started'),
                        });
                        return;
                    }

                    return useRepo(Application)
                        .whereIn('id', result.data.updated_application_ids)
                        .update({rejected: true});
                }).then(updates => {
                this.rejecting = false;
                this.performingRejectRequest = false;
                this.rejectData = {
                    reason: null,
                    comment: '',
                    comment_external: '',
                };
                if (!updates) return

                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('applications.mass_reject_success', {subject: this.$t('applications.selected_applications')}),
                });
                this.clearCheckedApplications([]);
            });
        },
        notReject() {
            this.performingNotRejectRequest = true;

            applications.massReject(this.getFilters, {
                applications: this.selectedApplicationIds,
                stages: this.selectedStageIds,
            }, {rejected: false, reject_reason: '', reject_comment: ''}, this.bulkKeyData.id)
                .then(result => {
                    if (result?.data?.message === 'started') {
                        this[ADD_NOTIFICATION]({
                            type: 'success',
                            message: this.$t('bulkaction.application.massUndoReject.started'),
                        });
                        return;
                    }

                    return useRepo(Application)
                        .whereIn('id', result.data.updated_application_ids)
                        .update({rejected: false});
                }).then(updates => {
                    this.notRejecting = false;
                    this.performingNotRejectRequest = false;

                    if (!updates) return

                    this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('applications.mass_not_reject_success', {subject: this.$t('applications.selected_applications')}),
                    });
                    this.clearCheckedApplications([]);
                });
        },
        confirmSubscribe() {
            this.subscribing = true;
            this.getBulkKey();
        },
        cancelSubscribe() {
            this.subscribing = false;
            this.bulkKeyData = null;
        },
        subscribe() {
            this.performingSubscribeRequest = true;

            applications.massSubscribe(this.getFilters, {
                applications: this.selectedApplicationIds,
                stages: this.selectedStageIds,
            }, this.bulkKeyData.id)
                .then(result => {

                    if (result?.data?.message === 'started') {
                        this[ADD_NOTIFICATION]({
                            type: 'success',
                            message: this.$t('bulkaction.application.massSubscribe.started'),
                        });
                        return;
                    }

                    result.data.updated_ids.forEach(ids => {
                        useRepo(Subscription).save(normalizeJson({
                            attributes: {
                                id: ids.subscription_id,
                                user_id: this.currentUserId,
                                company_id: ids.company_id,
                                subable_id: ids.application_id,
                                subable_type: 'applications',
                            },
                            type: 'subscriptions',
                        }));
                    });

                    return result;
                }).then(updates => {
                this.subscribing = false;
                this.performingSubscribeRequest = false;

                if (!updates) return

                this[ADD_NOTIFICATION](
                    {
                        type: 'success',
                        message: this.$t(
                            'applications.mass_subscribe_success',
                            {subject: this.$t('applications.selected_applications')},
                        ),
                    });
            });
        },
        confirmUnsubscribe() {
            this.unsubscribing = true;
            this.getBulkKey();
        },
        cancelUnsubscribe() {
            this.unsubscribing = false;
            this.bulkKeyData = null;
        },
        unsubscribe() {
            this.performingUnsubscribeRequest = true;

            applications.massUnsubscribe(this.getFilters, {
                applications: this.selectedApplicationIds,
                stages: this.selectedStageIds,
            }, this.bulkKeyData.id)
                .then(result => {
                    if (result?.data?.message === 'started') {
                        this[ADD_NOTIFICATION]({
                            type: 'success',
                            message: this.$t('bulkaction.application.massUnsubscribe.started'),
                        });
                        return;
                    }

                    let ids = [];

                    result.data.updated_ids.forEach(subscriptions => {
                        ids.push(subscriptions.subscription_id);
                    });

                    useRepo(Subscription).whereIn('id', ids).delete();

                    return result;
                }).then(updates => {
                this.unsubscribing = false;
                this.performingUnsubscribeRequest = false;

                if (!updates) return

                this[ADD_NOTIFICATION](
                    {
                        type: 'success',
                        message: this.$t(
                            'applications.mass_unsubscribe_success',
                            {subject: this.$t('applications.selected_applications')},
                        ),
                    });
            });
        },
        startMovingApplications() {
            this.movingApplications = true;
            this.getBulkKey();
        },
        stopMovingApplications() {
            this.movingApplications = false;
            this.bulkKeyData = null;
        },
        toggleChangingSourceApplications(start = true) {
            this.changingSourceApplications = start;
            if(!start) {
                this.bulkKeyData = null;
            }
        },
        getBulkKey() {
            bulkApplicationKey.get({
                data: {
                    selection: {
                        applications: this.selectedApplicationIds,
                        stages: this.selectedStageIds,
                    },
                },
            }, this.getFilters).then(data => {
                this.bulkKeyData = data;
            });
        },
    }
};
</script>
